<template>
    <div
        v-if="!impersonating"
        class="fixed z-40 flex flex-col items-end right-6"
        :class="hasCreateButton ? 'bottom-24' : 'bottom-6'"
    >
        <div
            v-if="open"
            class="w-80 max-w-[calc(100vw-3rem)] mb-3 overflow-hidden bg-white rounded-lg shadow-xl"
            role="dialog"
            aria-labelledby="help-widget-title"
        >
            <div class="flex items-center justify-between px-4 py-3 bg-blue-600">
                <h2 id="help-widget-title" class="text-sm font-semibold text-white">Help</h2>
                <button type="button" class="text-xl leading-none text-white hover:text-blue-100" aria-label="Close" @click="close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div class="p-4">
                <p v-if="succeeded" class="text-sm text-gray-700">
                    Thanks, we received your question and will reply by email.
                </p>
                <form v-else @submit.prevent="submit">
                    <p class="mb-3 text-sm text-gray-600">We'll reply by email.</p>

                    <label class="block text-xs font-medium text-gray-700" for="help-subject">Subject</label>
                    <input
                        id="help-subject"
                        v-model="subject"
                        type="text"
                        class="block w-full mt-1 text-sm border-gray-300 rounded-md shadow-sm"
                    >
                    <p v-if="fieldError('subject')" class="mt-1 text-xs text-red-600">{{ fieldError('subject') }}</p>

                    <label class="block mt-3 text-xs font-medium text-gray-700" for="help-message">Message</label>
                    <textarea
                        id="help-message"
                        v-model="message"
                        rows="4"
                        class="block w-full mt-1 text-sm border-gray-300 rounded-md shadow-sm"
                    ></textarea>
                    <p v-if="fieldError('message')" class="mt-1 text-xs text-red-600">{{ fieldError('message') }}</p>
                    <p v-if="fieldError('page_url')" class="mt-1 text-xs text-red-600">{{ fieldError('page_url') }}</p>

                    <p v-if="formError" class="mt-3 text-xs text-red-600" role="alert">{{ formError }}</p>

                    <button type="submit" class="w-full mt-4 tw-button-blue" :disabled="submitting">
                        {{ submitting ? 'Sending…' : 'Send' }}
                    </button>
                </form>
            </div>
        </div>

        <button
            type="button"
            class="flex items-center justify-center text-2xl font-bold text-white bg-blue-600 rounded-full shadow-md h-14 w-14 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            :aria-expanded="open ? 'true' : 'false'"
            aria-label="Help"
            @click="toggle"
        >
            <span aria-hidden="true">?</span>
        </button>
    </div>
</template>

<script>
export default {
    props: {
        storeUrl: {
            type: String,
            required: true,
        },
        hasCreateButton: {
            type: Boolean,
            default: false,
        },
        impersonating: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            open: false,
            subject: '',
            message: '',
            errors: {},
            formError: '',
            submitting: false,
            succeeded: false,
        };
    },

    methods: {
        toggle() {
            if (this.open) {
                this.close();

                return;
            }

            this.open = true;
        },

        close() {
            this.open = false;
            this.succeeded = false;
            this.subject = '';
            this.message = '';
            this.errors = {};
            this.formError = '';
            this.submitting = false;
        },

        fieldError(field) {
            const messages = this.errors[field];

            if (!messages || !messages.length) {
                return '';
            }

            return messages[0];
        },

        submit() {
            this.submitting = true;
            this.errors = {};
            this.formError = '';

            axios.post(this.storeUrl, {
                subject: this.subject.trim() === '' ? null : this.subject,
                message: this.message,
                page_url: window.location.href,
            }).then(() => {
                this.succeeded = true;
            }).catch((error) => {
                const status = error.response ? error.response.status : 0;

                if (status === 422) {
                    this.errors = (error.response.data && error.response.data.errors) || {};

                    return;
                }

                if (status === 401) {
                    this.formError = 'Your session has expired. Please sign in again.';

                    return;
                }

                if (status === 403) {
                    this.formError = 'Your email is not verified.';

                    return;
                }

                if (status === 419) {
                    this.formError = 'This page was open too long and the security token is stale. Refresh the page, then send again.';

                    return;
                }

                if (status === 429) {
                    this.formError = 'Please wait a moment and try again.';

                    return;
                }

                this.formError = 'Something went wrong. Please try again.';
            }).then(() => {
                this.submitting = false;
            });
        },
    },
};
</script>
